import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { listAlbums, listTags } from '../services/galleryService';
import type { GalleryAlbum, GalleryTag } from '../services/galleryService';

export const galleryAlbumsKey = (aquariumId: string) => ['galleryAlbums', aquariumId] as const;
export const galleryTagsKey = (aquariumId: string) => ['galleryTags', aquariumId] as const;
export const galleryImagesKey = (aquariumId: string) => ['galleryImages', aquariumId] as const;

export interface UseGalleryListResult<T> {
  items: T[];
  isLoading: boolean;
  error: unknown;
  refetch: () => void;
}

export function useGalleryAlbums(aquariumId: string): UseGalleryListResult<GalleryAlbum> {
  const query = useQuery({
    queryKey: galleryAlbumsKey(aquariumId),
    queryFn: () => listAlbums(aquariumId),
    enabled: Boolean(aquariumId),
  });
  return {
    items: query.data ?? [],
    isLoading: query.isPending && Boolean(aquariumId),
    error: query.error,
    refetch: () => {
      void query.refetch();
    },
  };
}

export function useGalleryTags(aquariumId: string): UseGalleryListResult<GalleryTag> {
  const query = useQuery({
    queryKey: galleryTagsKey(aquariumId),
    queryFn: () => listTags(aquariumId),
    enabled: Boolean(aquariumId),
  });
  return {
    items: query.data ?? [],
    isLoading: query.isPending && Boolean(aquariumId),
    error: query.error,
    refetch: () => {
      void query.refetch();
    },
  };
}

/**
 * After images were deleted: re-reads everything of the aquarium that can show them
 * (gallery, journal, animals, timeline, comparisons — every query keyed by the
 * aquarium id) plus the per-image queries of the viewer.
 */
export function useInvalidateAfterImageDelete(aquariumId: string): () => void {
  const queryClient = useQueryClient();
  return useCallback(() => {
    void queryClient.invalidateQueries({
      predicate: (query) =>
        query.queryKey.includes(aquariumId) ||
        query.queryKey[0] === 'imageInsights' ||
        query.queryKey[0] === 'imageJournalReferences',
    });
  }, [queryClient, aquariumId]);
}

/** Re-reads gallery images (all filter variants), albums and tags after an edit. */
export function useInvalidateGallery(aquariumId: string): (what?: { albums?: boolean; tags?: boolean }) => void {
  const queryClient = useQueryClient();
  return useCallback(
    (what = {}) => {
      void queryClient.invalidateQueries({ queryKey: galleryImagesKey(aquariumId) });
      if (what.albums) void queryClient.invalidateQueries({ queryKey: galleryAlbumsKey(aquariumId) });
      if (what.tags) void queryClient.invalidateQueries({ queryKey: galleryTagsKey(aquariumId) });
    },
    [queryClient, aquariumId],
  );
}
